import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useI18n } from '../../../../utils/i18n'
import { loadSession } from '../../../../auth/state.js'
import { normalizeEmotion } from '../../../../utils/emotion'
import { usePresence } from '../../../../utils/presence'
import { safeStorageGet, safeStorageSet } from '../../../../utils/storage'
import { IS_MOBILE, fetchLastDeviceId } from './monitorWs'
import { useMonitorConnection } from './useMonitorConnection'

export function useMonitor() {
  const { t, tx, lang } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [nowTick, setNowTick] = useState(Date.now())
  const [wsConnected, setWsConnected] = useState(false)
  const [deviceOnline, setDeviceOnline] = useState(false)
  const [deviceId, setDeviceId] = useState('-')
  const [targetDeviceId, setTargetDeviceId] = useState(() => {
    const saved = safeStorageGet('monitor.targetDeviceId') || ''
    if (saved) return saved
    try {
      return String(new URLSearchParams(window.location.search).get('device-id') || '').trim()
    } catch {
      return ''
    }
  })
  const [messages, setMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [streamText, setStreamText] = useState('')
  const streamTextRef = useRef('')
  const [wsSessionId, setWsSessionId] = useState('')
  const [wsUrl, setWsUrl] = useState('')
  const [emotion, setEmotion] = useState('neutral')
  const [emotionEmoji, setEmotionEmoji] = useState('😶')
  const [eyeTheme, setEyeTheme] = useState(() => {
    const saved = safeStorageGet('monitor.eyeTheme') || 'robo'
    if (saved === 'esp32') return 'robo'
    return saved
  })
  const [eyeColor, setEyeColor] = useState(() => safeStorageGet('monitor.eyeColor') || '#ff9600')
  const [previewPoke, setPreviewPoke] = useState(0)
  const [connecting, setConnecting] = useState(false)
  const [retryIn, setRetryIn] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  const [chatMode, setChatMode] = useState(() => safeStorageGet('monitor.chatMode') || 'cloud')
  const [inputText, setInputText] = useState('')
  const [sending, setSending] = useState(false)
  const [deviceMeta, setDeviceMeta] = useState({ board: '-', version: '-', updatedAt: 0 })
  const [pipeline, setPipeline] = useState({
    phase: 'idle',
    lastStatus: null,
    lastStt: null,
    lastTts: null,
    lastAssistantProvider: '-',
    lastAssistantAt: 0,
    lastEmotionAt: 0,
  })
  const [showDebug, setShowDebug] = useState(false)
  const [showPreviewTools, setShowPreviewTools] = useState(false)
  const [debugEvents, setDebugEvents] = useState([])
  const wsRef = useRef(null)
  const messagesEndRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const countdownTimerRef = useRef(null)
  const pingTimerRef = useRef(null)
  const wsCandidateIndexRef = useRef(0)
  const manualCloseRef = useRef(false)
  const lastErrorRef = useRef({ msg: '', ts: 0 })
  const lastSentEyeColorRef = useRef('')
  const historyLoadedRef = useRef(false)
  const conversationIdRef = useRef('')
  useEffect(() => { conversationIdRef.current = safeStorageGet('monitor.agentConversationId') || '' }, [])

  const controlReady = wsConnected && deviceOnline
  const detailsPresence = usePresence(showDetails, { exitMs: 140 })
  const detailsExiting = detailsPresence.phase === 'exit'
  const debugPresence = usePresence(showDebug, { exitMs: 140 })
  const debugExiting = debugPresence.phase === 'exit'
  const initialPanel = useMemo(() => {
    try {
      if (String(window.location.pathname || '').startsWith('/test')) return 'chat'
    } catch {}
    const v = String(searchParams.get('panel') || '').toLowerCase()
    return v === 'story' ? 'story' : 'chat'
  }, [searchParams])
  const [leftPanel, setLeftPanel] = useState(initialPanel)
  const isPublicTest = useMemo(() => {
    try {
      return String(window.location.pathname || '').startsWith('/test')
    } catch {
      return false
    }
  }, [])
  const effectiveChatMode = isPublicTest ? 'cloud' : chatMode

  useEffect(() => {
    if (!isPublicTest) return
    setShowDetails(true)
  }, [isPublicTest])

  useEffect(() => {
    setLeftPanel(initialPanel)
  }, [initialPanel])

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: IS_MOBILE ? 'auto' : 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamText])

  const addMessage = (msg) => {
    setMessages(prev => [...prev.slice(-99), msg])
  }

  const resetStream = () => {
    streamTextRef.current = ''
    setStreamText('')
    setIsTyping(false)
  }

  const { connect } = useMonitorConnection({
    addMessage, countdownTimerRef, historyLoadedRef, lastErrorRef,
    manualCloseRef, pingTimerRef, reconnectTimerRef, resetStream, setConnecting,
    setDebugEvents, setDeviceId, setDeviceMeta, setDeviceOnline, setEmotion,
    setEmotionEmoji, setIsTyping, setMessages, setPipeline, setRetryIn,
    setStreamText, setWsConnected, setWsSessionId, setWsUrl, streamTextRef,
    targetDeviceId, tx, wsCandidateIndexRef, wsConnected, wsRef,
  })

  const sendChat = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : inputText).trim()
    if (!text || sending) return
    setSending(true); setInputText('')
    addMessage({ type: 'user', text, source: 'local', direction: 'outbound', createdAt: Date.now() })
    try {
      if (effectiveChatMode === 'cloud' && !isPublicTest) {
        const session = loadSession(); const token = String(session?.tokens?.accessToken || '')
        const headers = { 'content-type': 'application/json', accept: 'application/json' }
        if (token) headers.authorization = `Bearer ${token}`
        let id = conversationIdRef.current
        if (!id) { const r = await fetch('/api/miaoban/conversations', { method: 'POST', headers, body: JSON.stringify({ title: text.slice(0, 48) }) }); const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b?.error || `conversation_http_${r.status}`); id = String(b?.conversation?.id || b?.id || ''); if (!id) throw new Error('conversation_id_missing'); conversationIdRef.current = id; safeStorageSet('monitor.agentConversationId', id) }
        const r = await fetch(`/api/miaoban/conversations/${encodeURIComponent(id)}/agent`, { method: 'POST', headers, body: JSON.stringify({ content: text }) }); const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b?.error || `agent_http_${r.status}`)
        addMessage({ type: 'assistant', text: String(b?.assistant?.content || b?.content || ''), source: 'agent', direction: 'inbound', createdAt: Date.now() })
      } else { const ws = wsRef.current; if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('console_not_connected'); if (effectiveChatMode === 'device') { if (!deviceOnline) throw new Error('device_offline'); ws.send(JSON.stringify({ type: 'chat', text, mode: 'manual', lang })) } else ws.send(JSON.stringify({ type: 'chat_cloud', text, mode: 'manual', lang })) }
    } catch (e) { addMessage({ type: 'system', text: tx({ zh: '消息发送失败：{err}', en: 'Message failed: {err}' }, { err: String(e?.message || e) }) }) } finally { setSending(false) }
  }

  const sendEmotion = (preset, { sendToDevice } = { sendToDevice: true }) => {
    const e = normalizeEmotion(preset?.emotion || 'neutral')
    setEmotion(e)
    setEmotionEmoji(preset?.emoji || '😶')
    try {
      if (!sendToDevice) {
        addMessage({
          type: 'system',
          text: tx(
            { zh: '提示：设备不可控，已切换预览表情（未下发）：{e}', en: 'Note: device not controllable; preview emoji only (not sent): {e}' },
            { e }
          ),
        })
        return
      }
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        addMessage({ type: 'system', text: tx({ zh: '控制台未连接云端，无法下发', en: 'Not connected to cloud; cannot send' }) })
        return
      }
      if (!controlReady) {
        addMessage({
          type: 'system',
          text: tx({ zh: '设备不可控：请先让设备上线（按一次 BOOT）', en: 'Device not controllable: bring device online (press BOOT once)' }),
        })
        return
      }
      const target = (targetDeviceId || deviceId || '').trim()
      if (!target || target === '-') {
        addMessage({ type: 'system', text: tx({ zh: '缺少 device-id，无法下发到设备', en: 'Missing device-id; cannot send to device' }) })
        return
      }
      wsRef.current.send(JSON.stringify({ type: 'set_emotion', target_device_id: target, emotion: e, emoji: preset?.emoji || '' }))
      addMessage({ type: 'system', text: tx({ zh: '下发表情请求：{e}', en: 'Sending emoji request: {e}' }, { e }) })
    } catch (err) {
      console.error('sendEmotion failed:', err)
      addMessage({ type: 'system', text: tx({ zh: '下发失败: {err}', en: 'Send failed: {err}' }, { err: String(err) }) })
    }
  }

  const sendEyeColor = (color, { sendToDevice, silent } = { sendToDevice: true, silent: false }) => {
    const c = String(color || '').trim()
    if (!c) return

    if (!sendToDevice) {
      if (!silent)
        addMessage({
          type: 'system',
          text: tx(
            { zh: '提示：设备不可控，已切换预览颜色（未下发）：{c}', en: 'Note: device not controllable; preview color only (not sent): {c}' },
            { c }
          ),
        })
      return
    }

    try {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        addMessage({ type: 'system', text: tx({ zh: '控制台未连接云端，无法下发颜色', en: 'Not connected to cloud; cannot send color' }) })
        return
      }
      if (!controlReady) {
        addMessage({
          type: 'system',
          text: tx({ zh: '设备不可控：请先让设备上线（按一次 BOOT）', en: 'Device not controllable: bring device online (press BOOT once)' }),
        })
        return
      }
      const target = (targetDeviceId || deviceId || '').trim()
      if (!target || target === '-') {
        addMessage({ type: 'system', text: tx({ zh: '缺少 device-id，无法下发到设备', en: 'Missing device-id; cannot send to device' }) })
        return
      }
      wsRef.current.send(JSON.stringify({ type: 'set_eye_color', target_device_id: target, color: c }))
      if (!silent) addMessage({ type: 'system', text: tx({ zh: '下发颜色请求：{c}', en: 'Sending color request: {c}' }, { c }) })
    } catch (err) {
      console.error('sendEyeColor failed:', err)
      addMessage({ type: 'system', text: tx({ zh: '下发失败: {err}', en: 'Send failed: {err}' }, { err: String(err) }) })
    }
  }

  useEffect(() => {
    if (!controlReady) return
    const c = String(eyeColor || '').trim()
    if (!c) return
    if (lastSentEyeColorRef.current === c) return
    lastSentEyeColorRef.current = c
    sendEyeColor(c, { sendToDevice: true, silent: true })
  }, [controlReady, eyeColor])

  useEffect(() => {
    ;(async () => {
      let initialId = String(targetDeviceId || '').trim()

      if (!initialId) {
        try {
          initialId = String(searchParams.get('device-id') || '').trim()
        } catch {
          initialId = ''
        }
      }

      if (!initialId) {
        const last = await fetchLastDeviceId()
        if (last) initialId = last
      }

      // /test 入口：即使没填 device-id，也用一个固定 id 建立会话，保证“天气/位置/情绪”等云端能力可直接测试。
      if (!initialId && isPublicTest) {
        initialId = 'public-test'
      }

      if (initialId && initialId !== targetDeviceId) setTargetDeviceId(initialId)
      connect(initialId)
    })()
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }
      if (wsRef.current) {
        manualCloseRef.current = true
        wsRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    safeStorageSet('monitor.targetDeviceId', targetDeviceId || '')
  }, [targetDeviceId])

  useEffect(() => {
    safeStorageSet('monitor.chatMode', chatMode || 'cloud')
  }, [chatMode])

  useEffect(() => {
    safeStorageSet('monitor.eyeTheme', eyeTheme || 'robo')
  }, [eyeTheme])

  useEffect(() => {
    safeStorageSet('monitor.eyeColor', eyeColor || '#ff9600')
  }, [eyeColor])

  const clearMessages = () => {
    setMessages([{ type: 'system', text: tx({ zh: '已清空', en: 'Cleared' }) }])
  }

  const selectLeftPanel = (next) => {
    setLeftPanel(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'story') params.set('panel', 'story')
    else params.delete('panel')
    setSearchParams(params, { replace: true })
  }

  const formatTime = (timestamp) => {
    if (timestamp) return timestamp
    return new Date().toLocaleTimeString()
  }

  const controlHint = (() => {
    if (isPublicTest) {
      if (!wsConnected) {
        if (connecting) {
          return tx({ zh: '连接云端 WebSocket 中…（{url}）', en: 'Connecting to cloud WebSocket… ({url})' }, { url: wsUrl || '-' })
        }
        return tx({ zh: '云端 WebSocket 未连接（点“重连”）', en: 'Cloud WebSocket disconnected (click Reconnect)' })
      }
      if (deviceOnline) {
        return tx({
          zh: '云端已连接；设备在线：可测放歌/故事/推送表情',
          en: 'Cloud connected; device online: music/story/emoji push are available',
        })
      }
      return tx({
        zh: '云端已连接；设备离线也可测：地理位置 / 天气 / 对话 / 情绪（放歌需要设备在线）',
        en: 'Cloud connected; device can be offline for location/weather/chat/emotion (music requires device online)',
      })
    }
    if (controlReady) return tx({ zh: '就绪：可推送表情/对话', en: 'Ready: can send emoji/chat' })
    if (!wsConnected) {
      if (connecting) return tx({ zh: '控制台连接后端 WebSocket 中…（{url}）', en: 'Connecting to backend WebSocket… ({url})' }, { url: wsUrl || '-' })
      return tx({ zh: '控制台未连接后端 WebSocket', en: 'Console not connected to backend WebSocket' })
    }
    if (!deviceOnline) return tx({ zh: '设备未连入云端（按一次 BOOT 触发连接）', en: 'Device not online (press BOOT once to connect)' })
    return tx({ zh: '未就绪', en: 'Not ready' })
  })()
  const primaryReady = isPublicTest ? wsConnected : controlReady
  const primaryDotCls = (() => {
    if (isPublicTest) {
      if (wsConnected) return 'bg-emerald-500 animate-pulse'
      if (connecting) return 'bg-amber-500 animate-pulse'
      return 'bg-rose-500'
    }
    return controlReady ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
  })()


  return { clearMessages, connect, connecting, controlHint, controlReady, debugEvents,
    debugExiting, debugPresence, detailsExiting, detailsPresence, deviceId,
    deviceMeta, deviceOnline, effectiveChatMode, emotion, emotionEmoji, eyeColor,
    eyeTheme, inputText, isPublicTest, isTyping, lastSentEyeColorRef, leftPanel,
    messages, messagesEndRef, nowTick, pipeline, previewPoke, primaryDotCls,
    primaryReady, retryIn, searchParams, selectLeftPanel, sendChat, sendEmotion,
    sendEyeColor, sending, setChatMode, setEyeColor, setEyeTheme, setInputText,
    setPreviewPoke, setSearchParams, setShowDebug, setShowDetails,
    setShowPreviewTools, setTargetDeviceId, showDebug, showDetails,
    showPreviewTools, streamText, t, targetDeviceId, tx, wsConnected, wsRef,
    wsSessionId, wsUrl }
}
